import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import classNames from 'classnames'

import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Icon } from '~/components/atoms/Icon/Icon'
import Link from '~/components/atoms/Link/Link'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import ArticlesFilter from '~/components/blocks/ArticlesFilter/ArticlesFilter'
import CategoryCalloutArticles from '~/components/blocks/CategoryCalloutArticles/CategoryCalloutArticles'
import FallbackPagination from '~/components/blocks/FallbackPagination/FallbackPagination'
import { resolveBlock } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import type { ArticlesPageData } from '~/utils/articles/getArticlesPageProps'
import { getArticlesPageProps } from '~/utils/articles/getArticlesPageProps'
import type { IPaginationContext } from '~/utils/doPagination'
import { addTranslations } from '~/utils/i18n'
import { supportedLocales } from '~/utils/langPaths'
import uniqueObjects from '~/utils/uniqueObjects'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/pages.module.css'

// Dynamic imports
const ArticlesList = dynamic(
  () => import('~/components/blocks/ArticlesList/ArticlesList'),
  { ssr: true }
)
const Breadcrumb = dynamic(
  () => import('~/components/atoms/Breadcrumb/Breadcrumb'),
  { ssr: true }
)
const RelatedArticles = dynamic(
  () => import('~/components/blocks/RelatedArticles/RelatedArticles'),
  { ssr: true }
)
const CategoryHero = dynamic(
  () => import('~/components/blocks/CategoryHero/CategoryHero'),
  { ssr: true }
)
const MagazineHero = dynamic(
  () => import('~/components/blocks/MagazineHero/MagazineHero'),
  { ssr: true }
)

const SubCategoryListing = dynamic(
  () => import('~/components/blocks/SubCategoryListing/SubCategoryListing'),
  { ssr: true }
)
const MagazineDropdown = dynamic(
  () => import('~/components/atoms/MagazineDropdown/MagazineDropdown'),
  { ssr: true }
)

// Analytics events configuration
const articlesListEvents = {
  article: {
    method: SearchAnalystMethods.convertedObjectIDs,
    name: SearchAnalystEventNames.articlesArticleClicked,
  },
  articleLarge: {
    method: SearchAnalystMethods.convertedObjectIDs,
    name: SearchAnalystEventNames.articlesArticleLargeClicked,
  },
  filter: {
    method: SearchAnalystMethods.convertedFilters,
    name: SearchAnalystEventNames.articlesArticleTypeClicked,
  },
}

export const CategoryArticlesPage: NextPage<ArticlesPageData> = (
  props: ArticlesPageData
) => {
  const { query, asPath } = useRouter()

  const isLargeDevice = useMediaQuery(`(min-width: ${bp.bpLarge})`)

  const {
    articles,
    breadcrumb,
    featuredArticles,
    settings,
    articleArtists,
    articlesTypeSlug,
    articlesType,
    featuredCategories,
    otherFilterCats,
    categorySpecificContent,
    isArtistIndex,
    artistIndexContent,
    indexSecondaryCategories,
    categoryArtistArticles,
    theme,
    totalArticles,
    totalPerPage,
    pageNumber = 1,
    lang,
  } = props

  const isMainIndex = Boolean(
    !articlesTypeSlug && articlesType !== 'category' && !isArtistIndex
  )
  const isHubContentVisible =
    categorySpecificContent &&
    (query?.filter === undefined || query?.filter === '')
  const nextPageNumber = pageNumber === 0 ? 2 : pageNumber + 1

  const categoriesForFilters = isMainIndex
    ? uniqueObjects(
        [
          ...featuredCategories,
          ...otherFilterCats,
          ...indexSecondaryCategories,
        ],
        'slug'
      )
    : uniqueObjects([...otherFilterCats], 'slug')

  const featuredArticleIds =
    articlesType === 'category'
      ? categorySpecificContent?.featuredArticles?.map(article => article._id)
      : featuredArticles?.map(article => article._id)

  const isVideo = ['videos', 'video'].includes(
    categorySpecificContent?.title.toLowerCase() || ''
  )
  const isPodcast = ['podcasts', 'podcast'].includes(
    categorySpecificContent?.title.toLowerCase() || ''
  )

  const renderPagination = () => {
    if (articlesTypeSlug) return null
    return (
      <FallbackPagination
        total={totalArticles}
        limit={totalPerPage}
        queryParams={{
          slug: 'articles',
          nextPage: nextPageNumber,
        }}
      />
    )
  }

  const hasSubCategories =
    categorySpecificContent?.subCategories &&
    categorySpecificContent.subCategories.length > 0

  console.log(
    'categorySpecificContent',
    categorySpecificContent?.calloutArticles
  )

  return (
    <>
      {!isMainIndex && (
        <MagazineDropdown
          featuredCategories={featuredCategories}
          articleArtists={articleArtists}
          noBorder={theme === 'dark'}
          selectedSlug={
            categorySpecificContent
              ? categorySpecificContent.slug
              : artistIndexContent?.slug
          }
          selectedArtistName={
            isArtistIndex ? artistIndexContent?.name : undefined
          }
        />
      )}

      <Breadcrumb list={breadcrumb} />
      {categorySpecificContent ? (
        <CategoryHero
          name={categorySpecificContent.title}
          summary={categorySpecificContent.summary}
          podcastInfo={categorySpecificContent?.podcastInfo}
        />
      ) : isArtistIndex ? (
        <CategoryHero
          name={`${artistIndexContent?.name || 'Artist'} articles`}
          summary={''}
        />
      ) : (
        <MagazineHero />
      )}

      <div
        className={classNames(
          categorySpecificContent?.theme
            ? styles[`articlesBg${categorySpecificContent.theme}`]
            : styles.articlesBg
        )}
      >
        {isMainIndex && (
          <MagazineDropdown
            featuredCategories={featuredCategories}
            articleArtists={articleArtists}
          />
        )}

        {hasSubCategories && (
          <SubCategoryListing
            categories={categorySpecificContent.subCategories}
            testID="sub-category-listing"
          />
        )}

        {categorySpecificContent?.calloutArticles &&
          categorySpecificContent.calloutArticles.length > 0 && (
            <CategoryCalloutArticles
              calloutArticles={categorySpecificContent.calloutArticles}
            />
          )}

        {(isMainIndex ||
          (isHubContentVisible &&
            categorySpecificContent?.featuredArticles)) && (
          <RelatedArticles
            title={`Featured ${isVideo ? 'videos' : isPodcast ? 'podcasts' : 'reads'}`}
            articles={
              categorySpecificContent?.featuredArticles ?? featuredArticles
            }
            hideViewAll={!categorySpecificContent}
            theme={ComponentTheme.primary}
            disableFilters
            hideBrowse
            hideTitle={false}
            noPaddingBottom
            key={`featured-reads-${asPath}`}
          />
        )}

        <div id="all-articles">
          <Container gutters={gutterTypes.always}>
            <Fixture>
              {query?.type && !isLargeDevice && (
                <div className={styles.articlesReturn}>
                  <Link href="/articles">
                    <span className={styles.articlesChevron}>
                      <Icon type="Chevron" height={10} width={10} />
                    </span>
                    <Text size={TextSizes.bodySml}>All articles</Text>
                  </Link>
                </div>
              )}
            </Fixture>
            {isMainIndex && (
              <Text
                size={TextSizes.primaryTitleLrg}
                className={styles.latestArticlesTitle}
                Tag="h4"
              >
                Latest articles
              </Text>
            )}
          </Container>
        </div>

        {isHubContentVisible &&
          categorySpecificContent?.body &&
          lang == 'en' && (
            <div className={styles.body}>
              {categorySpecificContent.body.map((block, i) =>
                resolveBlock(block, i, categorySpecificContent?.theme)
              )}
            </div>
          )}

        {categoriesForFilters && (
          <ArticlesFilter
            categoryList={categoriesForFilters}
            articleArtists={
              categoryArtistArticles ? categoryArtistArticles : articleArtists
            }
            consolidateFilters={isMainIndex}
            hideArtist={isArtistIndex}
            isArtistIndex={isArtistIndex}
            articlesTypeSlug={articlesTypeSlug}
            theme={categorySpecificContent?.theme}
            addSpacerTop={theme === 'dark'}
          />
        )}

        <ArticlesList
          articles={articles}
          magazineIndexBanner={
            isMainIndex ? settings.magazineIndexBanner : undefined
          }
          inListingBanners={isMainIndex ? settings.inListingBanners : undefined}
          myPortfolioBanner={
            isMainIndex ? settings.myPortfolioBanner : undefined
          }
          idFilterList={featuredArticleIds}
          pageNumber={pageNumber}
          articlesTypeSlug={articlesTypeSlug}
          searchInsightsEvents={articlesListEvents}
          useRowView={theme === 'dark'}
          grided={theme === 'dark'}
        />

        {renderPagination()}

        {categorySpecificContent?.pageBuilderModules2 && lang == 'en' && (
          <div className={styles.body}>
            {categorySpecificContent.pageBuilderModules2.map((block, i) =>
              resolveBlock(block, i)
            )}
          </div>
        )}
      </div>
    </>
  )
}

export const getStaticPropsOriginal = async function getStaticPropsOriginal(
  context: GetStaticPropsContext,
  localizedData: {
    settings: any
    allArtists: any
    totalArtists: number
    seoTemplates: any
  }
): Promise<GetStaticPropsResult<any>> {
  const { lang } = context.params as any
  const validLanguages = supportedLocales

  if (!validLanguages.includes(lang)) {
    return {
      notFound: true,
    }
  }

  try {
    const categoryParam = context.params?.category as string | undefined

    // Get SEO templates from centralized _app.tsx loading
    const seoTemplates = (context as any)?.pageProps?.seoTemplates
    const pageProps = await getArticlesPageProps(
      context as IPaginationContext,
      seoTemplates,
      categoryParam
    )

    const categoryProps = pageProps.categorySpecificContent as any

    const isArtistGenre =
      context.params?.category?.includes('artist-') ||
      context.params?.category?.includes('genre-')

    if (context.params?.category?.includes('artist-')) {
      return {
        notFound: true,
      }
    }

    if (
      !isArtistGenre &&
      (!categoryProps?._id || categoryProps?._id === null)
    ) {
      return {
        notFound: true,
      }
    }
    return {
      props: {
        ...pageProps,
        lang,
        notFound: false,
      },
    }
  } catch (error) {
    console.error('Error in getStaticProps:', error)

    return {
      notFound: true,
    }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)

    if ('props' in result) {
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

export const getStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
})

export default CategoryArticlesPage
